import { sceneConfig } from "../config/scene";
import { THREE } from "../dependencies";
import { palette, frameTokens } from "../theme";
import { destinations } from "./sculpture";
import type {
	BoxGeometry,
	MeshBasicMaterial,
	MeshStandardMaterial,
} from "three";

export function createScene(stage: HTMLElement) {
	const renderer = new THREE.WebGLRenderer({
		antialias: sceneConfig.renderer.antialias,
		alpha: sceneConfig.renderer.alpha,
	});
	// Match the intro's enlarged size without reallocating buffers during docking.
	renderer.setPixelRatio(
		Math.min(devicePixelRatio, sceneConfig.renderer.maxPixelRatio) *
			(window.pixelIntro?.renderScale || 1),
	);
	renderer.setClearColor(palette.page, sceneConfig.renderer.clearAlpha);
	renderer.outputColorSpace = THREE.SRGBColorSpace;
	renderer.shadowMap.enabled = sceneConfig.shadows.enabled;
	renderer.shadowMap.type = THREE.PCFSoftShadowMap;
	renderer.shadowMap.autoUpdate = false;
	stage.appendChild(renderer.domElement);
	const scene = new THREE.Scene();
	const camera = new THREE.OrthographicCamera(
		-sceneConfig.camera.expandedFraming,
		sceneConfig.camera.expandedFraming,
		sceneConfig.camera.expandedFraming,
		-sceneConfig.camera.expandedFraming,
		sceneConfig.camera.near,
		sceneConfig.camera.far,
	);
	// One continuous hit area per link includes the spaces between its glyphs.
	const linkBounds = new Map<
		string,
		{ bounds: InstanceType<typeof THREE.Box3>; label: string }
	>();
	for (const voxel of destinations) {
		if (!voxel.href) continue;
		let link = linkBounds.get(voxel.href);
		if (!link) {
			link = {
				bounds: new THREE.Box3(),
				label: voxel.linkLabel ?? voxel.href,
			};
			linkBounds.set(voxel.href, link);
		}
		link.bounds.expandByPoint(
			new THREE.Vector3(
				voxel.x - voxel.w / 2,
				voxel.y - voxel.h / 2,
				voxel.z - voxel.w / 2,
			),
		);
		link.bounds.expandByPoint(
			new THREE.Vector3(
				voxel.x + voxel.w / 2,
				voxel.y + voxel.h / 2,
				voxel.z + voxel.w / 2,
			),
		);
	}
	// These meshes are only used for raycasting, never added to the rendered scene.
	const linkHitboxes = [...linkBounds].map(([href, { bounds, label }]) => {
		bounds.expandByScalar(sceneConfig.sculpture.hitboxPadding);
		const size = bounds.getSize(new THREE.Vector3());
		const mesh = new THREE.Mesh(
			new THREE.BoxGeometry(size.x, size.y, size.z),
			new THREE.MeshBasicMaterial({ side: THREE.DoubleSide }),
		);
		mesh.position.copy(bounds.getCenter(new THREE.Vector3()));
		mesh.updateMatrixWorld(true);
		return { href, label, mesh };
	});
	const raycaster = new THREE.Raycaster();
	const pointer = new THREE.Vector2();
	scene.add(
		new THREE.HemisphereLight(
			sceneConfig.lighting.color,
			palette["scene-ambient"],
			sceneConfig.lighting.ambientIntensity,
		),
	);
	const light = new THREE.DirectionalLight(
		sceneConfig.lighting.color,
		sceneConfig.lighting.directionalIntensity,
	);
	light.position.set(
		sceneConfig.lighting.position.x,
		sceneConfig.lighting.position.y,
		sceneConfig.lighting.position.z,
	);
	light.castShadow = sceneConfig.shadows.enabled;
	light.shadow.mapSize.set(
		sceneConfig.shadows.mapSize,
		sceneConfig.shadows.mapSize,
	);
	light.shadow.camera.left = -sceneConfig.shadows.cameraExtent;
	light.shadow.camera.right = sceneConfig.shadows.cameraExtent;
	light.shadow.camera.top = sceneConfig.shadows.cameraExtent;
	light.shadow.camera.bottom = -sceneConfig.shadows.cameraExtent;
	light.shadow.normalBias = sceneConfig.shadows.normalBias;
	light.shadow.intensity = sceneConfig.shadows.intensity;
	scene.add(light);
	const world = new THREE.Group();
	scene.add(world);
	const flatSlabMaterial = new THREE.MeshBasicMaterial({
		color: palette["qr-paper"],
		toneMapped: false,
	});
	const litSlabMaterial = new THREE.MeshStandardMaterial({
		color: palette["qr-paper"],
		roughness: sceneConfig.base.roughness,
	});
	const slab = new THREE.Mesh<
		BoxGeometry,
		MeshBasicMaterial | MeshStandardMaterial
	>(
		new THREE.BoxGeometry(
			sceneConfig.base.width,
			sceneConfig.base.height,
			sceneConfig.base.depth,
		),
		flatSlabMaterial,
	);
	slab.position.y =
		-sceneConfig.base.topOffset -
		(sceneConfig.base.height / 2) * sceneConfig.base.flatScaleY;
	slab.receiveShadow = true;
	slab.castShadow = false;
	world.add(slab);
	// Flat, unlit layers match the project's crisp CSS border and offset shadow.
	function cardLayer(color: string) {
		const mesh = new THREE.Mesh(
			new THREE.PlaneGeometry(1, 1),
			new THREE.MeshBasicMaterial({ color }),
		);
		mesh.rotation.x = -Math.PI / 2;
		world.add(mesh);
		return mesh;
	}
	const border = cardLayer(palette.ink);
	border.name = "card-border";
	const cardShadow = cardLayer(palette.line);
	cardShadow.name = "card-shadow";
	function updateCardFrame(unitsPerPixel: number) {
		const borderSize = 2 * frameTokens.borderWidth * unitsPerPixel;
		const shadowOffset = frameTokens.shadowOffset * unitsPerPixel;
		const width = sceneConfig.base.width + borderSize;
		const depth = sceneConfig.base.depth + borderSize;
		border.scale.set(width, depth, 1);
		border.position.y = -sceneConfig.base.topOffset - 0.001;
		cardShadow.scale.set(width, depth, 1);
		cardShadow.position.set(
			shadowOffset,
			slab.position.y -
				(sceneConfig.base.height / 2) * slab.scale.y -
				0.002,
			shadowOffset,
		);
	}

	return {
		renderer,
		scene,
		camera,
		linkHitboxes,
		raycaster,
		pointer,
		world,
		slab,
		flatSlabMaterial,
		litSlabMaterial,
		updateCardFrame,
	};
}
